import { createKeyedSignal } from '@react-rxjs/utils'
import { Dispatch, useCallback, useEffect, useLayoutEffect, useState } from 'react'
import {
  filter,
  fromEvent,
  map,
  Observable,
  of,
  scan,
  share,
  startWith,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs'

import { CustomError, logAndCaptureException } from '../utils/errorTools'
import { getPageState, pageStateChanges$ } from '../utils/pageState'
import { localOrFallbackStorage, sessionOrFallbackStorage } from '../utils/storage'

export type UseLocalStorage = [value: string, setItem: Dispatch<string>, clearItem: () => void]
const isSSR = typeof window === 'undefined'
const useIsomorphicLayoutEffect = isSSR ? useEffect : useLayoutEffect

const [localStorageState$, setLocalStorageState] = createKeyedSignal<string, string | null>()
const [sessionStorageState$, setSessionStorageState] = createKeyedSignal<string, string | null>()

// Export keyed signals for programmatic updates
export { setLocalStorageState, setSessionStorageState }

class StorageEventRateLimitError extends CustomError {
  override name = 'StorageEventRateLimitError'

  constructor(
    public readonly count: number,
    public readonly key: string,
    public readonly since: number,
    public readonly storage: 'local' | 'session',
  ) {
    super(
      `Storage event exceeded rate limit (count: ${count}, key: ${key}, since: ${since}, storage: ${storage})`,
    )
  }
}

// map of shared event listeners by cache key (e.g. `local:event-key` or `session:event-key`)
const filteredEventsByKey = new Map<string, Observable<StorageEvent & { key: string }>>()

const storageEventForKey$ = ({
  key,
  storage,
}: {
  key: string
  storage: {
    getItem(key: string): string | null
    setItem(key: string, value: string): void
    removeItem(key: string): void
  }
}): Observable<StorageEvent & { key: string }> => {
  const cacheKey = storage === sessionOrFallbackStorage ? `session:${key}` : `local:${key}`
  const existing$ = filteredEventsByKey.get(cacheKey)
  if (existing$) {
    return existing$
  }
  const event$ = isSSR
    ? of()
    : fromEvent<StorageEvent>(window, 'storage').pipe(
        filter((event): event is StorageEvent & { key: string } =>
          storage === sessionOrFallbackStorage
            ? event.storageArea === window.sessionStorage && event.key === key
            : event.storageArea === window.localStorage && event.key === key,
        ),
        share(),
      )
  filteredEventsByKey.set(cacheKey, event$)
  return event$
}

function subscribeToStorageChanges({
  key,
  storage,
  updateComponentState,
  getComponentState,
}: {
  key: string
  storage: {
    getItem(key: string): string | null
    setItem(key: string, value: string): void
    removeItem(key: string): void
  }
  updateComponentState: (newValue: string | null) => void
  getComponentState: () => string
}) {
  // Choose the proper state signal based on storage type
  const state$ = storage === sessionOrFallbackStorage ? sessionStorageState$ : localStorageState$
  const setState =
    storage === sessionOrFallbackStorage ? setSessionStorageState : setLocalStorageState

  const subscriptions = [
    storageEventForKey$({ key, storage })
      .pipe(
        switchMap((event) => {
          const initialPageState = getPageState()
          // If window/tab is visible (state is active or passive), process storage event.
          // Otherwise, wait for window to become visible before processing.
          return initialPageState === 'active' || initialPageState === 'passive'
            ? of(event)
            : pageStateChanges$.pipe(
                filter((pageState) => pageState === 'active' || pageState === 'passive'),
                take(1),
                map(() => event),
              )
        }),
        filter((event) => event.newValue !== event.oldValue),
        withLatestFrom(state$(key).pipe(startWith(storage.getItem(key)))),
        filter(([event, lastState]) => event.newValue !== lastState),
        map(([event]) => event),
      )
      .subscribe((event) => setState(event.key, event.newValue || '')),

    state$(key)
      .pipe(filter((newValue) => storage.getItem(key) !== newValue))
      .subscribe((newValue) =>
        newValue === null ? storage.removeItem(key) : storage.setItem(key, newValue),
      ),

    state$(key)
      .pipe(filter((newValue) => getComponentState() !== newValue))
      .subscribe((newValue) => updateComponentState(newValue || '')),
  ]
  return () => subscriptions.forEach((subscription) => subscription.unsubscribe())
}

/**
 * Monitor for excessive events and capture Sentry exception if limit reached
 */
function subscribeToMonitorExcessiveEvents({
  key,
  storage,
}: {
  key: string
  storage: {
    getItem(key: string): string | null
    setItem(key: string, value: string): void
    removeItem(key: string): void
  }
}): () => void {
  const subscription = storageEventForKey$({ key, storage })
    .pipe(
      filter((event) => event.newValue !== event.oldValue),
      // monitor change count over a 5 second window and capture exception if limit exceeded
      scan<
        StorageEvent & { key: string },
        {
          changeCount: number
          exceptionTime: null | number
          windowStart: number
        },
        { windowStart: null }
      >(
        (acc, _event) => {
          const now = Date.now()
          // if current window started more than 5 seconds ago, discard and start a new window
          if (acc.windowStart == null || acc.windowStart < now - 5000) {
            return { changeCount: 1, exceptionTime: null, windowStart: now }
          }
          const {
            changeCount: prevChangeCount,
            exceptionTime: prevExceptionTime,
            windowStart,
          } = acc
          const changeCount = prevChangeCount + 1
          // consider excessive if more than 100 events received in 5 seconds
          const overLimit = changeCount > 100
          // if last exception time is null or more than 5 minutes ago, capture a new exception
          const captureException =
            overLimit && (!prevExceptionTime || prevExceptionTime < now - 5 * 60 * 100)
          if (captureException) {
            logAndCaptureException(
              new StorageEventRateLimitError(
                changeCount,
                key,
                windowStart,
                storage === sessionOrFallbackStorage ? 'session' : 'local',
              ),
            )
          }
          return {
            changeCount,
            exceptionTime: !overLimit ? null : captureException ? now : prevExceptionTime,
            windowStart,
          }
        },
        { windowStart: null },
      ),
    )
    .subscribe()

  return () => subscription.unsubscribe()
}

export type UseStorageParams = {
  key: string
  defaultValue?: string
  type?: 'local' | 'session'
}

export const useStorage = ({
  key,
  defaultValue = '',
  type = 'local',
}: UseStorageParams): UseLocalStorage => {
  const storage = type === 'session' ? sessionOrFallbackStorage : localOrFallbackStorage
  const setStorageState = type === 'session' ? setSessionStorageState : setLocalStorageState

  const storedValue = storage.getItem(key) || ''
  const [value, setValue] = useState<string>(storedValue || defaultValue)

  const clearItem = useCallback(() => setStorageState(key, null), [key, setStorageState])
  const setItem = useCallback(
    (newValue: string) => setStorageState(key, newValue),
    [key, setStorageState],
  )

  useIsomorphicLayoutEffect(() => {
    const unsubscribe = subscribeToStorageChanges({
      key,
      storage,
      updateComponentState: (newValue) => setValue(newValue || ''),
      getComponentState: () => value,
    })
    return unsubscribe
  }, [key, value, storage])

  useEffect(() => subscribeToMonitorExcessiveEvents({ key, storage }), [key, storage])

  return [value, setItem, clearItem]
}
